import React, { useState, useEffect } from "react";
import { Form, Modal } from "antd";
import { Link } from "react-router-dom";

import SignUpForm from "components/sign-up-form/sign-up-form";
import GoogleAuthButton from "components/google-auth-button/google-auth-button";

import routeNames from "constants/route-names";
import { GoogleAuthStatus } from "constants/google-auth-status";
import { useSignUp } from "hooks/useSignUp";
import { useBrand } from "context/BrandContext";

interface SignUpModalProps {
	open: boolean;
	onClose: () => void;
}

const SignUpModal = ({ open, onClose }: SignUpModalProps) => {
	const { brand } = useBrand();
	const [form] = Form.useForm();
	const [hasSignedUp, setHasSignedUp] = useState<boolean>(false);
	// While Google is resolving a brand new account, the email/password form is hidden: it is
	// not a second way to finish that same flow.
	const [googleAuthStatus, setGoogleAuthStatus] = useState<GoogleAuthStatus>(GoogleAuthStatus.IDLE);
	const isGoogleAuthActive = googleAuthStatus !== GoogleAuthStatus.IDLE;

	// Extract guideId from current URL if it exists
	const searchParams = new URLSearchParams(window.location.search);
	const guideId = searchParams.get("guideId");

	// Use the shared sign-up hook with success callback
	const signUpHook = useSignUp({
		guideId,
		onSignUpSuccess: () => setHasSignedUp(true)
	});

	// Reset form when modal closes
	useEffect(() => {
		if (!open) {
			form.resetFields();
			signUpHook.resetForm();
			setHasSignedUp(false);
		}
	}, [open, form]);

	// Show verification message after successful sign-up
	if (hasSignedUp) {
		return (
			<Modal
				rootClassName={`brand-${brand.toLowerCase()}`}
				className="sign-up-modal"
				open={open}
				onCancel={onClose}
				footer={null}
				centered
			>
				<div className="sign-up-modal-container">
					<img
						src={`${process.env.PUBLIC_URL}/assets/images/icons/highnote-login-icon.svg`}
						alt="Highnote Logo"
						className="sign-up-modal-logo"
					/>
					<div>
						<div className="sign-up-modal-title">We just sent an email with a link to verify your address.</div>
						<div className="sign-up-modal-title">Please check your inbox or spam folder.</div>
					</div>

					<span className="sign-up-modal-link-label">
						Need assistance?
						<a className="inline-link bold-link" href="mailto:support@highnotes.com?subject=Trouble Signing Up">
							Contact Us
						</a>
					</span>
				</div>
			</Modal>
		);
	}

	// Show sign-up form
	return (
		<Modal
			rootClassName={`brand-${brand.toLowerCase()}`}
			className="sign-up-modal"
			open={open}
			onCancel={onClose}
			footer={null}
			centered
		>
			<div className="sign-up-modal-container">
				<img
					src={`${process.env.PUBLIC_URL}/assets/images/icons/highnote-login-icon.svg`}
					alt="Highnote Logo"
					className="sign-up-modal-logo"
				/>
				<div>
					<div className="sign-up-modal-title">Create an account to interact with this Guide. Let's get started!</div>
					<div className="sign-up-modal-subtitle">
						Learn more about this step-by-step guide with AI and share your feedback!
					</div>
				</div>

				<GoogleAuthButton
					termsAccepted={signUpHook.termsAccepted}
					onAuthenticated={onClose}
					onStatusChange={setGoogleAuthStatus}
					className="sign-up-modal-google-auth"
				/>

				{!isGoogleAuthActive && (
					<>
						<SignUpForm
							form={form}
							firstName={signUpHook.firstName}
							lastName={signUpHook.lastName}
							email={signUpHook.email}
							phoneNumber={signUpHook.phoneNumber}
							password={signUpHook.password}
							termsAccepted={signUpHook.termsAccepted}
							setFirstName={signUpHook.setFirstName}
							setLastName={signUpHook.setLastName}
							setEmail={signUpHook.setEmail}
							setPassword={signUpHook.setPassword}
							setTermsAccepted={signUpHook.setTermsAccepted}
							handleOnChangePhoneNumber={signUpHook.handleOnChangePhoneNumber}
							onSubmit={signUpHook.createUser}
							loading={signUpHook.loading}
							error={signUpHook.signUpError}
							formClassName="sign-up-modal-form"
							formItemClassName="sign-up-modal-form-item"
							termsCheckboxClassName="sign-up-modal-terms-checkbox"
							termsLabelClassName="sign-up-modal-terms-label"
						/>

						<span className="sign-up-modal-link-label">
							Already have an account?
							<Link
								to={{
									pathname: routeNames.LOGIN
								}}
								className="inline-link bold-link"
								rel="noopener"
							>
								Sign In.
							</Link>
						</span>
					</>
				)}
			</div>
		</Modal>
	);
};

export default SignUpModal;
